/**
 * Public marketing landing page served at `/`.
 *
 * Data-free front door: routes candidates to /portal/login and staff to
 * /dashboard/login. No live platform data is fetched. The hero product
 * card is an illustrative mock (labelled), built from the design tokens.
 *
 * Aesthetic — "Product-forward enterprise" (light): a clean white canvas
 * with considered depth, a realistic product visual as the hero anchor,
 * Mona Sans display + IBM Plex Mono labels, brand blue for action, green
 * the single payoff accent. Full-bleed so the logo is strictly leftmost.
 */

import { type CSSProperties, type ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components/Wordmark';

// Official Mediant Labs logo (Std_Logo.svg), inlined so it renders without
// depending on a served static asset. IDs are scoped per-instance via
// useId so the header and footer copies don't collide.
function MediantLogo({ height = 22, className = '' }: { height?: number; className?: string }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const grad = `ml-g-${uid}`, c0 = `ml-c0-${uid}`, c1 = `ml-c1-${uid}`;
  return (
    <svg width={Math.round((height * 262) / 47)} height={height} viewBox="0 0 262 47" fill="none" role="img" aria-label="Mediant Labs" className={className}>
      <g clipPath={`url(#${c0})`}>
        <path d="M115.76 25.0131L115.724 10.0054L108.436 22.3678H105.853L98.5996 10.3268V25.0131H93.2217V0H97.9631L107.233 15.5441L116.361 0H121.067L121.138 25.0131H115.76Z" fill="#193661" />
        <path d="M145.302 20.3672V25.0121H126.126V0H144.843V4.64491H131.823V10.0765H143.321V14.5792H131.823V20.3672H145.302Z" fill="#193661" />
        <path d="M160.609 20.2598C165.528 20.2598 168.747 17.2943 168.747 12.5059C168.747 7.71782 165.528 4.75205 160.609 4.75205H155.372V20.2598H160.609ZM149.641 0H160.892C168.994 0 174.55 4.93028 174.55 12.5059C174.55 20.0815 168.994 25.0121 160.892 25.0121H149.641V0Z" fill="#193661" />
        <path d="M177.982 0H183.713V25.0121H177.982V0Z" fill="#193661" />
        <path d="M203.208 15.2574L199.281 5.68089L195.353 15.2574H203.208ZM205.013 19.6527H193.513L191.32 25.0131H185.447L196.486 0H202.147L213.221 25.0131H207.206L205.013 19.6527Z" fill="#193661" />
        <path d="M237.633 0V25.0121H232.928L220.58 9.82583V25.0121H214.918V0H219.66L231.972 15.1863V0H237.633Z" fill="#193661" />
        <path d="M247.754 4.716H239.828V0H261.41V4.716H253.485V25.0121H247.754V4.716Z" fill="#193661" />
        <path d="M93.2217 32.4519H95.2554V45.0481H102.967V46.836H93.2217V32.4519Z" fill="#193661" />
        <path d="M113.221 41.5969L110.149 34.548L107.076 41.5969H113.221ZM113.932 43.24H106.364L104.798 46.836H102.703L109.151 32.4532H111.165L117.635 46.836H115.499L113.932 43.24Z" fill="#193661" />
        <path d="M129.436 42.7876C129.436 41.124 128.215 40.384 125.956 40.384H121.278V45.1708H125.956C128.215 45.1708 129.436 44.4529 129.436 42.7876ZM121.278 34.1168V38.7191H125.448C127.523 38.7191 128.704 37.9387 128.704 36.4173C128.704 34.8969 127.523 34.1168 125.448 34.1168H121.278ZM131.49 42.952C131.49 45.4178 129.639 46.836 126.039 46.836H119.243V32.4519H125.631C128.907 32.4519 130.738 33.8493 130.738 36.1719C130.738 37.7333 129.944 38.8222 128.825 39.3764C130.433 39.8285 131.49 41.0414 131.49 42.952Z" fill="#193661" />
        <path d="M132.834 45.1916L133.587 43.5885C134.645 44.5547 136.455 45.2534 138.287 45.2534C140.749 45.2534 141.806 44.2885 141.806 43.0346C141.806 39.5209 133.18 41.74 133.18 36.3978C133.18 34.1773 134.89 32.2879 138.633 32.2879C140.301 32.2879 142.03 32.7399 143.21 33.5613L142.539 35.2057C141.298 34.4048 139.894 34.0341 138.633 34.0341C136.212 34.0341 135.174 35.0622 135.174 36.3151C135.174 39.8284 143.8 37.6302 143.8 42.9116C143.8 45.1102 142.05 47 138.287 47C136.11 47 133.974 46.2609 132.834 45.1916Z" fill="#193661" />
      </g>
      <g clipPath={`url(#${c1})`}>
        <path d="M0 46.6244H83.6724L52.7851 0.628269L33.818 29.7602L49.097 29.5949L53.9047 22.3202L62.993 35.6792H22.1612L38.8891 10.7798L31.2825 0L0 46.6244Z" fill={`url(#${grad})`} />
      </g>
      <defs>
        <linearGradient id={grad} x1="41.8362" y1="0" x2="41.8362" y2="46.6244" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2484C6" />
          <stop offset="1" stopColor="#1DAA58" />
        </linearGradient>
        <clipPath id={c0}><rect width="169" height="47" fill="white" transform="translate(93)" /></clipPath>
        <clipPath id={c1}><rect width="83.6724" height="46.6244" fill="white" /></clipPath>
      </defs>
    </svg>
  );
}

const CANDIDATE_LOGIN = '/portal/login';
const STAFF_LOGIN = '/dashboard/login';
// Full-bleed: padding only, no centred cap — content (and the logo) hugs
// the left edge instead of sitting inside a narrower centred column.
const SHELL = 'w-full px-6 sm:px-10 lg:px-16 2xl:px-24';

const CARD_SHADOW = '0 1px 2px rgba(25,54,97,0.04), 0 18px 40px -22px rgba(25,54,97,0.16)';
const LIFT_SHADOW = '0 8px 18px rgba(25,54,97,0.07), 0 44px 80px -34px rgba(25,54,97,0.30)';
const eyebrow = 'font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--color-primary-ink)]';

// ── CTAs ─────────────────────────────────────────────────────────────────
function BlueCTA({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="inline-flex min-h-[50px] items-center justify-center rounded-[10px] bg-[var(--color-primary)] px-6 font-sans text-[15px] font-semibold text-white shadow-[0_1px_2px_rgba(25,54,97,0.12)] transition-[transform,box-shadow,background-color] duration-150 hover:-translate-y-px hover:bg-[var(--color-primary-hover)] hover:shadow-[0_10px_24px_-8px_rgba(36,132,198,0.5)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]">
      {children}
    </Link>
  );
}
function GhostCTA({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="group inline-flex min-h-[50px] items-center justify-center gap-2 rounded-[10px] border border-[var(--color-rule-strong)] bg-white px-6 font-sans text-[15px] font-semibold text-[var(--color-ink)] transition-colors duration-150 hover:border-[var(--color-primary)] hover:text-[var(--color-primary-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]">
      {children}
      <span aria-hidden className="transition-transform duration-150 group-hover:translate-x-0.5">&rarr;</span>
    </Link>
  );
}

// ── Icons (inline, brand line-style) ──────────────────────────────────────
const ico = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconFramework = () => (<svg {...ico} aria-hidden><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>);
const IconSliders = () => (<svg {...ico} aria-hidden><line x1="4" y1="8" x2="20" y2="8" /><circle cx="9" cy="8" r="2" /><line x1="4" y1="16" x2="20" y2="16" /><circle cx="15" cy="16" r="2" /></svg>);
const IconArchive = () => (<svg {...ico} aria-hidden><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><line x1="10" y1="12" x2="14" y2="12" /></svg>);

// ── Platform-workflow content (from prism_workflow.html) ──────────────────
const PIPELINE = [
  { id: 'S1', name: 'Batch parsing', ai: 76 },
  { id: 'S2', name: 'Weighted scoring', ai: 72 },
  { id: 'S3', name: 'Assessment', ai: 68 },
  { id: 'S4', name: 'Portfolio build', ai: 55 },
  { id: 'S5', name: 'Interview', ai: 28 },
  { id: 'S6', name: 'Triangulation', ai: 48 },
  { id: 'S7', name: 'Professional Passport', ai: 22 },
];
const JD_STEPS = [
  { name: 'JD ingestion', desc: 'Any format, any role family — parsed into structured requirements.', tag: 'Input', green: false },
  { name: 'Competency set & weightage', desc: 'Derived against the taxonomy; each competency weighted by criticality to the role.', tag: 'Derived', green: true },
  { name: 'Batch parsing · custom set', desc: 'Resumes plotted against role-specific competencies, not the standard framework.', tag: 'S1 adapted', green: false },
  { name: 'Custom assessment', desc: 'Built from the derived competency set, with the same structural rigour.', tag: 'S3 adapted', green: true },
  { name: 'Distilled candidate list', desc: 'A ranked pool with detailed competency profiles specific to this role.', tag: 'Output', green: false },
];
const INDICATORS = [
  { n: '1', label: 'Competency criticality', desc: 'How critical each competency is to the JD — derived from role requirements and weighted at derivation. Governs scoring priority across the pipeline.', green: true },
  { n: '2', label: 'Candidate evidence strength', desc: 'How strongly each candidate evidences the competencies from their resume. Surfaces coverage gaps and strength signals against the role-specific set.', green: false },
];

const PROFILE = [
  { d: 'Analysis', fill: 4, band: 'Proficient', tone: 'good' },
  { d: 'Design', fill: 5, band: 'Exemplary', tone: 'good' },
  { d: 'Development', fill: 4, band: 'Proficient', tone: 'good' },
  { d: 'Implementation', fill: 3, band: 'Developing', tone: 'warn' },
  { d: 'Evaluation', fill: 4, band: 'Proficient', tone: 'good' },
];

function ProfileCard() {
  return (
    <div className="w-full max-w-[520px] rounded-[16px] border border-[var(--color-rule)] bg-white" style={{ boxShadow: LIFT_SHADOW }}>
      <div className="flex items-center justify-between border-b border-[var(--color-rule)] px-5 py-3.5">
        <div className="flex items-center gap-3">
          <span aria-hidden className="flex gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-[var(--color-rule-strong)]" /><i className="h-2.5 w-2.5 rounded-full bg-[var(--color-rule-strong)]" /><i className="h-2.5 w-2.5 rounded-full bg-[var(--color-rule-strong)]" /></span>
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-ink-soft)]">Competency profile</span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-success)]/30 bg-[var(--color-success-soft)] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-success-ink)]">
          <i className="h-1.5 w-1.5 rounded-full bg-[var(--color-success)]" /> Assessor-reviewed
        </span>
      </div>
      <div className="px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-sans text-[16px] font-semibold text-[var(--color-ink)]">Instructional Designer</p>
            <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">Tier 2 · Intermediate</p>
          </div>
          <div className="rounded-[8px] bg-[var(--color-surface-muted)] px-3 py-2 text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">Triangulated</p>
            <p className="font-sans text-[15px] font-semibold tabular-nums text-[var(--color-ink)]">4.2<span className="text-[var(--color-ink-faint)]">/5</span></p>
          </div>
        </div>
        <div className="mt-6 space-y-3.5">
          {PROFILE.map((r) => (
            <div key={r.d} className="grid grid-cols-[110px_1fr_auto] items-center gap-3">
              <span className="font-sans text-[13px] text-[var(--color-ink-muted)]">{r.d}</span>
              <span aria-hidden className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (<i key={i} className="h-2 flex-1 rounded-[2px]" style={{ background: i < r.fill ? 'var(--color-primary)' : 'var(--color-bg-deep)' }} />))}
              </span>
              <span className={['inline-flex justify-center rounded-full px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.1em]', r.tone === 'good' ? 'bg-[var(--color-success-soft)] text-[var(--color-success-ink)]' : 'bg-[var(--color-warn-soft)] text-[var(--color-warn-ink)]'].join(' ')}>{r.band}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-[var(--color-rule)] px-6 py-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">Illustrative preview</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">Human-reviewed before release</span>
      </div>
    </div>
  );
}

export function Landing() {
  const heroWash: CSSProperties = {
    backgroundImage: 'radial-gradient(55% 55% at 100% 0%, rgba(0,141,165,0.09), transparent 60%), radial-gradient(45% 50% at 0% 25%, rgba(36,132,198,0.06), transparent 55%)',
  };
  return (
    <div className="min-h-screen bg-white text-[var(--color-ink)]">
      {/* ── Header — logo flush-left; both links in the left cluster ── */}
      <header className="sticky top-0 z-[var(--z-sticky)] border-b border-[var(--color-rule)] bg-white">
        <div className={`flex h-[68px] items-center gap-5 ${SHELL}`}>
          <Link to="/" aria-label="Prism home" className="rounded-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-primary)]">
            <Wordmark size={24} />
          </Link>
          <span aria-hidden className="h-6 w-px bg-[var(--color-rule)]" />
          <MediantLogo height={22} />
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={heroWash}>
        <div className={`grid items-center gap-14 py-16 lg:grid-cols-[1fr_1.02fr] lg:gap-16 lg:py-24 ${SHELL}`}>
          <div className="prism-enter">
            <p className={eyebrow}>Competency-based hiring assessment</p>
            <h1 className="mt-5 font-sans text-[clamp(40px,5.4vw,66px)] font-bold leading-[1.02] tracking-[-0.035em] text-[var(--color-ink)]">
              Hire on <span className="text-[var(--color-success)]">evidence</span>,<br className="hidden sm:block" /> not instinct.
            </h1>
            <p className="mt-6 max-w-[46ch] font-sans text-[clamp(16px,1.4vw,18.5px)] leading-[1.6] text-[var(--color-ink-muted)]">
              Prism gives L&amp;D teams a defensible read on every candidate — a competency profile
              triangulated from portfolio, interview, and resume evidence, signed off by a human assessor.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <BlueCTA to={CANDIDATE_LOGIN}>Candidate sign-in</BlueCTA>
              <GhostCTA to={STAFF_LOGIN}>Staff access</GhostCTA>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-[0.13em] text-[var(--color-ink-soft)]">
              {['Human-reviewed', 'Audit-trailed', 'Model-neutral', 'Consent-managed'].map((t, i) => (
                <span key={t} className="flex items-center gap-3">{i > 0 && <span aria-hidden className="text-[var(--color-rule-strong)]">·</span>}{t}</span>
              ))}
            </div>
          </div>
          <div className="prism-enter flex justify-center lg:justify-end" style={{ animationDelay: '120ms' }}>
            <ProfileCard />
          </div>
        </div>
      </section>

      {/* ── The platform workflow (from prism_workflow.html, in light) ── */}
      {/* Contained in one padded card (capped width) so the pipeline boxes
          don't stretch full-bleed on wide screens. */}
      <section className="border-t border-[var(--color-rule)] bg-white">
        <div className={`py-20 lg:py-24 ${SHELL}`}>
          <div className="mx-auto max-w-[1320px] rounded-[24px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] p-6 sm:p-10 lg:p-14" style={{ boxShadow: CARD_SHADOW }}>
          <div className="max-w-[60ch]">
            <p className={eyebrow}>The platform workflow</p>
            <h2 className="mt-4 font-sans text-[clamp(28px,3.6vw,44px)] font-bold leading-[1.08] tracking-[-0.025em] text-[var(--color-ink)]">
              From candidate to <span className="text-[var(--color-success)]">verified practitioner</span>.
            </h2>
            <p className="mt-4 max-w-[64ch] font-sans text-[15.5px] leading-relaxed text-[var(--color-ink-muted)]">
              One auditable pipeline, human-in-the-loop at every stage. The bar under each stage shows how much is
              AI-automated versus assessor-owned.
            </p>
          </div>

          {/* Pipeline */}
          <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-ink-faint)]">Pipeline</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {PIPELINE.map((s) => (
              <div key={s.id} className="rounded-[12px] border border-[var(--color-rule)] bg-white p-4" style={{ boxShadow: CARD_SHADOW }}>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-faint)]">{s.id}</span>
                <p className="mt-2 font-sans text-[13px] font-semibold leading-tight text-[var(--color-ink)]">{s.name}</p>
                <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-[var(--color-bg-deep)]" role="img" aria-label={`${s.ai}% AI-automated, ${100 - s.ai}% human`}>
                  <span className="bg-[var(--color-success)]" style={{ width: `${s.ai}%` }} />
                  <span className="bg-[var(--color-primary)]" style={{ width: `${100 - s.ai}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.13em] text-[var(--color-ink-soft)]">
            <span className="flex items-center gap-2"><span aria-hidden className="h-[3px] w-6 rounded bg-[var(--color-success)]" /> AI-automated</span>
            <span className="flex items-center gap-2"><span aria-hidden className="h-[3px] w-6 rounded bg-[var(--color-primary)]" /> Human-reviewed</span>
          </div>

          {/* Foundation band */}
          <div className="mt-10 flex flex-col gap-4 rounded-[14px] border border-[var(--color-success)]/25 bg-[var(--color-success-soft)] p-5 sm:flex-row sm:items-center" style={{ boxShadow: CARD_SHADOW }}>
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] bg-[var(--color-success)] text-white"><IconFramework /></span>
            <div className="flex-1">
              <p className="font-sans text-[15px] font-semibold text-[var(--color-success-ink)]">Competency framework — the spine</p>
              <p className="mt-1 font-sans text-[13.5px] leading-relaxed text-[var(--color-success-ink)]/80">
                110 competencies &middot; 3 tiers &middot; IBSTPI &amp; ATD standards &middot; the scoring reference behind every stage.
              </p>
            </div>
            <span className="shrink-0 self-start rounded-full bg-[var(--color-success)] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-white sm:self-center">The spine</span>
          </div>

          {/* Custom workflow — Stage 0 */}
          <div className="mt-6 rounded-[16px] border border-[var(--color-rule)] bg-white p-6 lg:p-8" style={{ boxShadow: CARD_SHADOW }}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] bg-[var(--color-primary-soft)] text-[var(--color-primary)]"><IconSliders /></span>
              <div className="flex-1">
                <p className="font-sans text-[16px] font-semibold text-[var(--color-ink)]">Role-specific pipeline derivation</p>
                <p className="mt-0.5 font-sans text-[13.5px] text-[var(--color-ink-soft)]">Supply a job description — the pipeline reconfigures itself.</p>
              </div>
              <span className="shrink-0 self-start rounded-full bg-[var(--color-primary-soft)] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-primary-ink)] sm:self-center">Stage 0</span>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {JD_STEPS.map((s) => (
                <div key={s.name} className="rounded-[10px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] p-4">
                  <span className={['inline-block rounded-[6px] px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em]', s.green ? 'bg-[var(--color-success-soft)] text-[var(--color-success-ink)]' : 'bg-[var(--color-primary-soft)] text-[var(--color-primary-ink)]'].join(' ')}>{s.tag}</span>
                  <p className="mt-3 font-sans text-[13px] font-semibold leading-tight text-[var(--color-ink)]">{s.name}</p>
                  <p className="mt-1.5 font-sans text-[12px] leading-relaxed text-[var(--color-ink-soft)]">{s.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {INDICATORS.map((ind) => (
                <div key={ind.n} className="flex gap-4 rounded-[10px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] p-4">
                  <span className={['flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] font-mono text-[13px] font-semibold', ind.green ? 'bg-[var(--color-success-soft)] text-[var(--color-success-ink)]' : 'bg-[var(--color-primary-soft)] text-[var(--color-primary-ink)]'].join(' ')}>{ind.n}</span>
                  <div>
                    <p className="font-sans text-[13px] font-semibold text-[var(--color-ink)]">Indicator {ind.n} &middot; {ind.label}</p>
                    <p className="mt-1 font-sans text-[12px] leading-relaxed text-[var(--color-ink-soft)]">{ind.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-3 rounded-[10px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] p-4">
              <span aria-hidden className="mt-0.5 shrink-0 text-[var(--color-ink-faint)]"><IconArchive /></span>
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">Sunset criteria</p>
                <p className="mt-1 font-sans text-[12.5px] leading-relaxed text-[var(--color-ink-soft)]">
                  Candidate profiles are stored, updated, and archived against the role-specific competency set.
                  Retention, refresh, and archive thresholds are defined per engagement.
                </p>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* ── Two ways in (both light; the page's single closing CTA) ── */}
      <section className="border-t border-[var(--color-rule)] bg-[var(--color-surface-muted)]">
        <div className={`py-20 lg:py-24 ${SHELL}`}>
          <p className={eyebrow}>Two ways in</p>
          <h2 className="mt-4 font-sans text-[clamp(28px,3.6vw,44px)] font-bold tracking-[-0.025em] text-[var(--color-ink)]">Choose your door.</h2>
          <div className="mt-11 grid gap-5 lg:grid-cols-2">
            {/* Candidate */}
            <div className="relative flex flex-col overflow-hidden rounded-[16px] border border-[var(--color-rule)] bg-white p-9 lg:p-10" style={{ boxShadow: CARD_SHADOW }}>
              <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[var(--color-primary)]" />
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-primary-ink)]">For candidates</p>
              <h3 className="mt-3.5 max-w-[18ch] font-sans text-[clamp(24px,2.6vw,32px)] font-bold leading-[1.06] tracking-[-0.02em] text-[var(--color-ink)]">Show your work, not just your title.</h3>
              <p className="mt-3.5 flex-1 font-sans text-[15px] leading-relaxed text-[var(--color-ink-muted)]">Invited to an assessment? Sign in to submit your evidence and track your progress. You control your data and your consent throughout.</p>
              <div className="mt-9"><BlueCTA to={CANDIDATE_LOGIN}>Candidate sign-in</BlueCTA><p className="mt-3.5 font-mono text-[11px] uppercase tracking-[0.13em] text-[var(--color-ink-faint)]">By invitation — from your invite email</p></div>
            </div>
            {/* Staff — light, blue-tinted (no longer a dark block) */}
            <div className="relative flex flex-col overflow-hidden rounded-[16px] border border-[var(--color-primary)]/20 bg-[var(--color-primary-soft)] p-9 lg:p-10" style={{ boxShadow: CARD_SHADOW }}>
              <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[var(--color-teal)]" />
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-primary-ink)]">For assessors &amp; admins</p>
              <h3 className="mt-3.5 max-w-[18ch] font-sans text-[clamp(24px,2.6vw,32px)] font-bold leading-[1.06] tracking-[-0.02em] text-[var(--color-ink)]">Where evidence becomes a decision.</h3>
              <p className="mt-3.5 flex-1 font-sans text-[15px] leading-relaxed text-[var(--color-ink-muted)]">Review triangulated profiles, build custom-JD competency frameworks, manage the item bank, and sign off recommendations — with a complete audit trail behind every action.</p>
              <div className="mt-9"><BlueCTA to={STAFF_LOGIN}>Enter the staff workbench</BlueCTA><p className="mt-3.5 font-mono text-[11px] uppercase tracking-[0.13em] text-[var(--color-ink-soft)]">Provisioned by your administrator</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer — logo flush-left ── */}
      <footer className="border-t border-[var(--color-rule)] bg-white">
        <div className={`flex flex-col gap-6 py-10 lg:flex-row lg:items-center lg:justify-between ${SHELL}`}>
          <div className="flex items-center gap-4">
            <Wordmark size={20} />
            <span aria-hidden className="h-5 w-px bg-[var(--color-rule)]" />
            <MediantLogo height={18} />
          </div>
          <nav className="flex flex-wrap items-center gap-x-7 gap-y-2 font-sans text-[13.5px]" aria-label="Footer">
            <Link to={CANDIDATE_LOGIN} className="text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]">Candidate sign-in</Link>
            <Link to={STAFF_LOGIN} className="text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]">Staff access</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
